/**
 * SimulationForm.jsx
 * A React Component to manage staging a water or fragment run for simulation.
 *
 */

/* eslint camelcase: ["error", { allow: ["case_suggestion", "sim_spec"]}] */

import React, { useEffect, useState, useContext } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import DialogContentText from '@mui/material/DialogContentText';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';

// Local
import { UserActions } from 'BMapsCmds';
import { SimSpec } from '../../../model/SimSpec';
import { App } from '../../../BMapsApp';
import { molTypeToString } from '../../ui_utils';
import DialogContent from '../../common/DialogContent';
import DialogContentSection from '../../common/DialogContentSection';
import Select from '../../common/Select';
import { DialogSubmitButton } from '../../common/DialogActionButtons';
import { classifyResidue } from '../../../utils';
import ProteinSelect from '../../common/ProteinSelect';
import AdditionalStructures from './AdditionalStructures';
import { isPreparedSetupSource, defaultMinimizeBox } from './simSourceProfile';
import { ProteinContext } from '../../common/ProteinContextProvider';
import { Loader } from '../../../Loader';
import { alphaFoldSimSpecFilter } from '../../../util/alphafold_utils';
import { MapCase } from '../../../model/MapCase';
import { structureLabelsByUri, structureKey } from '../../../model/structureRef';
import ExternalLink from '../../common/ExternalLink';
import TooltipInfo from '../../common/TooltipInfo';
import { BfdMolTypeCodes } from '../../../molecule_types';
import { TextInputWithTooltip, BindingSiteSpecs, customBindingSiteIncompleteText } from './Components';

const ProjectNameRegex = /^[a-zA-Z0-9+._-]+$/;
const ProjectNameRegexDescription = 'Only letters, numbers, and the following special characters are allowed + . _ -';

// Cap on the number of target proteins one submission may fan out to.
// Soft-cap that mirrors the MAX_PROTEINS_PER_CART in services/services/shopping/views.py
// Keep the two values in sync!
const MAX_PROTEINS_PER_CART = 600; // allows all kinases

/**
 * What the Automatic binding site choice will do for this setup: bfd-server derives a binding
 * site from each compound in the simulation (and from cofactors without built-in definitions).
 */
export function automaticBindingSiteHint({ useSelection, compound, cofactors=[] }) {
    if (useSelection) {
        return 'Automatic: a 10 Å binding site around each compound in the selected atoms, if any.';
    }
    if (compound) {
        return `Automatic: a 10 Å binding site around ${compound.resSpec}.`;
    }
    const cofactorNote = cofactors.length > 0
        ? ' (Cofactors without a built-in definition still get one.)'
        : '';
    return 'Automatic: no binding site, since no compound is in the simulation.'
        + ` To focus on a pocket, choose Custom and pick a compound or other reference.${cofactorNote}`;
}

function ProteinSetupBox({
    loadedProteins, formOptions, formValues, updateSimInputs,
    isWaterSim, disabled, multiSelectDisabled,
    allowDefaultConfig, useDefaultConfig, onUseDefaultChange,
    selectedProteins, useExistingForAdditional,
    onToggleProtein, onSetSelectedProteins, onSetUseExisting,
}) {
    const usingDefault = allowDefaultConfig && useDefaultConfig;

    function getPolymerLabel(i) {
        // Avoid adding "Reference" moltype detail in polymer check labels (internal language only)
        const chainPart = `Chain ${i.chain}`;
        const excludedMolTypes = [
            BfdMolTypeCodes.ref_protein,
            BfdMolTypeCodes.ref_dna,
            BfdMolTypeCodes.ref_rna,
        ];
        const useMolType = !excludedMolTypes.includes(i.molType);
        const molTypePart = useMolType ? ` (${molTypeToString(i.molType)})` : '';
        return `${chainPart}${molTypePart}`;
    }

    return (
        <Paper
            style={{
                padding: '10px',
                height: '100%',
                minHeight: '80px',
                margin: '0 0 20px',
            }}
            variant="outlined"
        >
            <FormControl style={{ width: '100%' }}>
                <ProteinSelect
                    style={{ margin: '2px 0 14px 6px', paddingRight: '12px' }}
                    id="simulation-form_protein-select"
                    required
                />
                {/*
                  * Choosing which additional structures to also simulate is independent of
                  * whether the *primary* reuses its existing setup, so this gets its own
                  * disabled state rather than inheriting the primary-config lock (`disabled`).
                  */}
                <AdditionalStructures
                    disabled={multiSelectDisabled}
                    selectedProteins={selectedProteins}
                    useExistingForAdditional={useExistingForAdditional}
                    onToggleProtein={onToggleProtein}
                    onSetSelectedProteins={onSetSelectedProteins}
                    onSetUseExisting={onSetUseExisting}
                />
                {loadedProteins?.length === 1 && (
                    <Typography
                        variant="h6"
                        style={{ padding: '6px 0 14px 0px' }}
                    >
                        {formOptions.proteinType}
                        :
                        {' '}
                        {formOptions.proteinName}
                    </Typography>
                )}
                { !!allowDefaultConfig && (
                <FormControl>
                    <RadioGroup onChange={onUseDefaultChange}>
                        <FormControlLabel
                            control={<Radio checked={!!useDefaultConfig} />}
                            label={`Keep using the current simulation setup for ${formOptions.proteinName}`}
                            value
                        />
                        <FormControlLabel
                            control={<Radio checked={!useDefaultConfig} />}
                            label="Specify a new simulation setup"
                            value={false}
                        />
                    </RadioGroup>
                </FormControl>
                )}
                {(isWaterSim && formOptions.protein) && (
                    <CompoundSelect
                        compounds={formOptions.compounds}
                        selected={formValues.compound}
                        onChange={(cmp) => updateSimInputs({ compoundInput: cmp })}
                        disabled={disabled}
                        useDefaultConfig={usingDefault}
                    />
                )}
                {(formOptions.proteinChains?.length > 0
                    && formValues.proteinChains?.length === 0
                    && !disabled)
                    && (
                        <Typography sx={{ color: 'error.main', paddingBottom: '12px' }}>
                            Please select a
                            {' '}
                            {formOptions.proteinType}
                            {' '}
                            chain for the simulation
                        </Typography>
                    )}
                <Grid container spacing={4} style={{ width: '100%' }}>
                    {formOptions.proteinChains?.length > 0
                        && (
                            <Grid>
                                <CheckboxList
                                    label="Target Chains"
                                    items={formOptions.proteinChains}
                                    selected={formValues.proteinChains}
                                    getName={(i) => `Chain ${i.chain}`}
                                    onChange={(pc) => updateSimInputs({ proteinChain: pc })}
                                    disabled={disabled}
                                    useDefaultConfig={usingDefault}
                                />
                            </Grid>
                        )}
                    {formOptions.cofactors?.length > 0
                        && (
                            <Grid>
                                <CheckboxList
                                    label="Cofactors"
                                    items={formOptions.cofactors}
                                    selected={formValues.cofactors}
                                    getName={(i) => `${i.resSpec}`}
                                    onChange={(cf) => updateSimInputs({ cofactor: cf })}
                                    disabled={disabled}
                                    useDefaultConfig={usingDefault}
                                />
                            </Grid>
                        )}
                    {formOptions.polymers?.length > 0
                        && (
                            <Grid>
                                <CheckboxList
                                    label="Others"
                                    items={formOptions.polymers}
                                    selected={formValues.polymers}
                                    getName={(i) => getPolymerLabel(i)}
                                    onChange={(p) => updateSimInputs({ polymer: p })}
                                    disabled={disabled}
                                    useDefaultConfig={usingDefault}
                                />
                            </Grid>
                        )}
                    {formOptions.ions?.length > 0
                        && (
                            <Grid>
                                <CheckboxList
                                    label="Ions"
                                    items={formOptions.ions}
                                    selected={formValues.ions}
                                    getName={(i) => `${i.resSpec}`}
                                    onChange={(i) => updateSimInputs({ ion: i })}
                                    disabled={disabled}
                                    useDefaultConfig={usingDefault}
                                />
                            </Grid>
                        )}
                </Grid>
            </FormControl>
        </Paper>
    );
}

export default function SimulationForm({ formType, open, close }) {
    const initialState = {
        open: false,
        formValues: {
            proteinName: '',
            project: '',
            case: '',
            proteinChains: [],
            ions: [],
            cofactors: [],
            polymers: [],
            compounds: [],
            bindingSiteSpecs: '',
            autoBindingSite: true,
        },
        allowDefaultConfig: {
            water: false,
            fragment: false,
        },
    };
    const simFormTypes = {
        water: {
            type: 'water',
            isWater: true,
            isFragment: false,
            isClustering: false,
            allowGen2: false,
            allowSolvation: false,
        },
        fragment: {
            type: 'fragment',
            isWater: false,
            isFragment: true,
            isClustering: false,
            allowGen2: true,
            allowSolvation: true,
        },
        clustering: {
            type: 'clustering',
            isWater: false,
            isFragment: false,
            isClustering: true,
            allowGen2: false,
            allowSolvation: true,
        },
    };

    const formId = 'simulation-form';
    const permissionWarning = "You don't have permission for this feature. Please contact support.";

    const [simulationType, setSimulationType] = useState(simFormTypes[formType.type]);
    const [permissions, setPermissions] = useState({});
    const [formOptions, setFormOptions] = useState({});
    const [defaultConfig, setDefaultConfig] = useState(initialState.formValues);
    const [allowDefaultConfig, setAllowDefaultConfig] = useState(initialState.allowDefaultConfig);
    const [useDefaultConfig, setUseDefaultConfig] = useState(false);
    const [useSelection, setUseSelection] = useState(false);
    // Custom binding site chosen but not yet set: preparing would run another choice.
    const [bindingSiteIncomplete, setBindingSiteIncomplete] = useState(false);
    const [workspaceSelection, setWorkspaceSelection] = useState([]);
    const [prepared, setPrepared] = useState(false);
    const [keepSelection, setKeepSelection] = useState(false);
    const [filterAlphaFold, setFilterAlphaFold] = useState(false);
    const [formValues, setFormValues] = useState(initialState.formValues);
    const [warning, setWarning] = useState(false);
    const [fragservData, setFragservData] = useState(null);
    // Per-structure prep results for ADDITIONAL targets (beyond the primary),
    // for multi-protein runs: each { name, status, fragservArgs|null, error|null }.
    const [additionalPrepared, setAdditionalPrepared] = useState([]);
    const [formDisabled, setFormDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [enableSolvation, setEnableSolvation] = useState(false);
    const [minimizeBox, setMinimizeBox] = useState(false);
    const [gen2Annealing, setGen2Annealing] = useState(false);
    const {
        loadedProteins, selectedProtein, selectedCaseData,
    } = useContext(ProteinContext);

    // Multi-protein selection state -- simulation-specific, so it lives in the
    // feature rather than the generic ProteinContext. selectedProteins is the
    // ordered structure set to fan the simulation out over (primary + additionals);
    // useExistingForAdditional controls whether additionals reuse their existing
    // server-side setup instead of preparing fresh.
    const [selectedProteins, setSelectedProteins] = useState(
        selectedProtein ? [selectedProtein] : [],
    );
    const [useExistingForAdditional, setUseExistingForAdditional] = useState(true);

    function toggleSelectedProtein(p) {
        setSelectedProteins((list) => (
            list.includes(p) ? list.filter((x) => x !== p) : [...list, p]
        ));
    }

    // Reset the additional-structure list to just the primary whenever the primary
    // changes (previously ProteinContext.handleContextChange did this).
    useEffect(() => {
        setSelectedProteins(selectedProtein ? [selectedProtein] : []);
    }, [selectedProtein]);

    useEffect(() => {
        if (open) {
            openSimPane(formType);
        } else {
            clearForm();
        }
    }, [open]);

    useEffect(() => {
        if (selectedProtein) {
            deriveState(App.Workspace, simulationType.type, selectedCaseData);
        }
    }, [selectedProtein, selectedCaseData]);

    function submitClose() {
        close();
        clearForm();
    }

    function clearForm() {
        setSimulationType({});
        setFormOptions({});
        setDefaultConfig(initialState.formValues);
        setAllowDefaultConfig(initialState.allowDefaultConfig);
        setUseDefaultConfig(false);
        setUseSelection(false);
        setWorkspaceSelection([]);
        setPrepared(false);
        setFormValues(initialState.formValues);
        setWarning(false);
        setFragservData(null);
        setAdditionalPrepared([]);
        setFormDisabled(true);
        setLoading(false);
    }

    /**
     * Re-derive the structure name when the simulation type changes.
     *
     * The default names the simulation type (`_frags` / `_waters` / `_hotspots`) when it would
     * otherwise duplicate the structure's own case name, so it goes stale on a type switch --
     * a fragment run ends up called `..._hotspots`.
     *
     * Only a name WE generated is replaced: if the current value is not what the default would
     * have been for the previous type, the user typed it and it is left alone.
     */
    function updateCaseNameForSimType(simType, previousSimType) {
        if (!formOptions.protein) return;
        const {
            compound, proteinChains, ions, cofactors, polymers,
        } = formValues;
        const baseNameFor = (type) => SimSpec.defaultCaseName({
            protein: formOptions.protein, compound, simType: type,
        });
        const tag = getCaseNameTag({
            compound, proteinChains, ions, cofactors, polymers,
        }, formOptions);

        const previousBase = baseNameFor(previousSimType);
        const nextBase = baseNameFor(simType);
        // Don't replace with a blank case name
        if (!nextBase) return;

        // formOptions.case holds the bare default, and "Use Selection" appends `_selection` to
        // it rather than to the live value. It is seeded once in deriveState, so without this it
        // keeps whichever type the form was opened with, and a later Use Selection toggle
        // resurrects it. Updated unconditionally: it is the default, not the user's name.
        setFormOptions((prev) => ({ ...prev, case: nextBase }));

        if (!SimSpec.isGeneratedCaseName(formValues.case, previousBase, tag)) return;
        // Keep whichever convention the current value follows: the seeded name carries no tag,
        // a name rebuilt by an input handler does.
        const wasTagged = !!tag && formValues.case === `${previousBase}${tag}`;
        setFormValues((prev) => ({ ...prev, case: wasTagged ? `${nextBase}${tag}` : nextBase }));
    }

    function handleSimTypeChange(evt) {
        const simType = evt.target.value;
        // Captured before setSimulationType: this render's state is still the outgoing type.
        const previousSimType = simulationType?.type;
        if (allowDefaultConfig[simType]) {
            defaultConfig.fragments = fragmentsToSend(simType);
        }
        // Consider keeping user's choice if they move away from default option
        const useSolvation = simType === 'clustering';
        setEnableSolvation(useSolvation);
        setSimulationType(simFormTypes[simType]);
        const usingDefault = allowDefaultConfig[simType] && useDefaultConfig;
        setFragservData(usingDefault ? defaultConfig : null);
        setFormDisabled(usingDefault);
        setPrepared(usingDefault);
        handleWarnings(simType);
        setUseSelection(false);
        updateCaseNameForSimType(simType, previousSimType);
    }

    function openSimPane(data) {
        const { type } = data;
        // Remove any existing fragserv data
        setFragservData(null);
        setWarning(false);
        // Set things that cannot be changed by the form
        const waterPermission = App.Ready && App.AllPermissions.isAllowed('water_run');
        const fragmentPermission = App.Ready && App.AllPermissions.isAllowed('frag_run');
        setPermissions({
            water: waterPermission,
            fragment: fragmentPermission,
            clustering: waterPermission && fragmentPermission,
        });
        if ((type === 'water' && !waterPermission) || (type === 'fragment' && !fragmentPermission)) {
            setWarning({ warning: permissionWarning });
        }
    }

    function reorderByChain(list) {
        return list.sort((a, b) => {
            if (a.chain < b.chain) return -1;
            if (a.chain > b.chain) return 1;
            else return 0;
        });
    }

    function handleWarnings(type) {
        if (!permissions[type]) {
            setWarning({ warning: permissionWarning });
        } else if (warning) {
            setWarning(false);
        }
    }

    /**
     * Right now this only includes water for water runs.
     * Theoretically it could check other values, like a fragment picker?
     */
    function fragmentsToSend(simType) {
        switch (simType) {
            case 'water':
                return 'water';
            case 'clustering': {
                const clusteringFrags = App.Workspace.fragmentData.getClusteringFragset()?.items;
                if (!clusteringFrags || clusteringFrags.length === 0) {
                    return '';
                }
                const fragNames = clusteringFrags.map(({ name }) => name);
                return fragNames.join('\n');
            }
            default:
                return '';
        }
    }

    function deriveState(newWorkspace, simType, newCaseData) {
        // Acquire the sim spec, set the main protein for the form
        const includeActiveCompound = (simType === 'water') && newWorkspace.getActiveCompound();
        const fragments = fragmentsToSend(simType);
        const initial_sim_spec = SimSpec.GetFromWorkspace(
            newWorkspace, newCaseData, includeActiveCompound, fragments
        );
        const protein = newCaseData.mapCase;
        const isAlphaFoldStructure = protein.sourceDesc === MapCase.Sources.AlphaFold;

        // Determine if protein has a default spec
        const protSourceDesc = protein.sourceDesc;
        const shouldUseDefaultSpec = {
            water: protSourceDesc === MapCase.Sources.Fragserv,
            fragment: isPreparedSetupSource(protSourceDesc),
            clustering: protSourceDesc === MapCase.Sources.Fragserv,
        };
        // Consider keeping user's choice if they move away from default option
        const useSolvation = simType === 'clustering';
        setEnableSolvation(useSolvation);
        // minimizeBox defaults from the structure's own prep metadata when present (the
        // value it was originally prepared/run with), else the per-source default. Managed
        // (and any non-Public) curated structures have no minimize box by default; once
        // their simulation-meta.json is backfilled, the metadata value takes over.
        // sim-meta lives on the fragment series, not the case, so match the series to this
        // structure by datasource (per-session mount index) and reconcile across them; an
        // unmatched or divergent result falls back to the per-source default.
        const structureMountIndex = protein.getDataSource()?.mountIndex;
        const existingSimMeta = newCaseData.getAvailableFragmentInfo()
            ?.simMetaForMountIndex(structureMountIndex) ?? null;
        const useMinimizeBox = defaultMinimizeBox(existingSimMeta, protSourceDesc);
        setMinimizeBox(useMinimizeBox);
        setAllowDefaultConfig(shouldUseDefaultSpec);
        // Generate config data, regardless of if it is used on this SimType
        setDefaultConfig(SimSpec.fragservArgsFromProtein(protein, fragments));
        // In the event of selecting different protein
        setUseSelection(false);
        // Only allow Use Selection if atoms come from the selected protein
        const selectedAtoms = newWorkspace.getSelectedAtoms();
        const { dataParentsList } = App.partitionByDataParents(selectedAtoms);
        if (dataParentsList.every(({ caseData }) => caseData === newCaseData)) {
            setWorkspaceSelection(selectedAtoms);
        } else {
            setWorkspaceSelection([]);
        }
        // Set up state for either sim type
        setSimulationType(simFormTypes[simType]);

        const defaultConfigExists = isPreparedSetupSource(protSourceDesc);
        const defaultUseDefault = defaultConfigExists;
        setUseDefaultConfig(defaultUseDefault);
        const allowingDefault = shouldUseDefaultSpec[simType];
        const usingDefault = allowingDefault && defaultUseDefault;

        setFormDisabled(usingDefault);
        setPrepared(usingDefault);
        setFragservData(allowingDefault && SimSpec.fragservArgsFromProtein(protein, fragments));

        setFormOptions({
            proteinName: initial_sim_spec.protein.getLongName(),
            proteinChains: reorderByChain(newCaseData.getProteinChains()),
            proteinType: classifyResidue({
                resname: newCaseData.getProteinChains()[0]?.residue.name,
                rnaValue: 'RNA',
                dnaValue: 'DNA',
                proteinValue: 'Protein',
            }),
            protein: initial_sim_spec.protein,
            ions: reorderByChain(newCaseData.getIons()),
            cofactors: reorderByChain(newCaseData.getCofactors()),
            polymers: reorderByChain(newCaseData.getPolymers()),
            compounds: newCaseData.getCompounds(),
            project: initial_sim_spec.project,
            case: SimSpec.defaultCaseName({
                protein: initial_sim_spec.protein, compound: null, simType,
            }),
            isAlphaFoldStructure,
        });
        // const chainNames = initial_sim_spec.proteinChains.map((i) => i.chain);
        setFormValues({
            project: initial_sim_spec.project,
            case: SimSpec.defaultCaseName({
                protein: initial_sim_spec.protein, compound: null, simType,
            }),
            proteinName: initial_sim_spec.protein.getLongName(),
            compound: null,
            proteinChains: initial_sim_spec.proteinChains,
            ions: initial_sim_spec.ions,
            cofactors: initial_sim_spec.cofactors,
            polymers: initial_sim_spec.polymers,
        });
    }

    function handleUseSelectionToggle(checked) {
        setUseSelection(checked);
        if (checked) {
            setFragservData(null);
            setFormDisabled(true);
            // Currently making a simplifying assumption that only one of
            // "Filter AlphaFold" and "Use Selection" can be checked
            // This is also reflected in the "disabled" prop on the Checkboxes
            setFilterAlphaFold(false);
            setFormValues({
                ...formValues,
                project: formOptions.project,
                case: `${formOptions.case}_selection`,
            });
        } else {
            if (allowDefaultConfig[simulationType.type] && useDefaultConfig) {
                setFragservData(defaultConfig);
                setFormDisabled(true);
            } else {
                setFragservData(null);
                setFormDisabled(false);
                updateSimInputs({});
            }
        }
    }

    function handleFilterAlphaFoldToggle(checked) {
        if (checked) {
            setFilterAlphaFold(true);
            // Currently making a simplifying assumption that only one of
            // "Filter AlphaFold" and "Use Selection" can be checked.
            // This is also reflected in the "disabled" prop on the Checkboxes
            handleUseSelectionToggle(false);
        } else {
            setFilterAlphaFold(false);
        }
    }

    function handleDefaultConfigToggle(evt) {
        const useDefault = evt.target.value === 'true';
        setUseDefaultConfig(useDefault);
        if (useDefault) {
            setFragservData(defaultConfig);
            setFormDisabled(true);
            setPrepared(true);
        } else {
            setFragservData(null);
            setFormDisabled(false);
            setPrepared(false);
        }
        setWarning(false);
    }

    function updateCaseOrProject(type, newName) {
        if (type === 'case') {
            setFormValues({
                ...formValues,
                case: newName,
            });
        } else if (type === 'project') {
            setFormValues({
                ...formValues,
                project: newName,
            });
        }
    }

    function getCaseNameTag(newVals, allVals) {
        /** Return a modification tag for ion/cofactor, based on the expected selection */
        function getModTag(selected, all, selectedChains, allChains, name) {
            // Expect ions and cofactors whose chains are selected or who are not a protein chain
            const expected = all.filter(
                ({ chain }) => selectedChains.includes(chain) || !allChains.includes(chain)
            );

            if (selected.length === 0) {
                return expected.length > 0 ? `-no-${name}` : '';
            }

            const missing = expected.filter((f) => !selected.includes(f));
            const added = selected.filter((f) => !expected.includes(f));

            if (missing.length > 0 && added.length > 0) {
                return `-custom-${name}`;
            } else if (missing.length > 0) {
                return `-less-${name}`;
            } else if (added.length > 0) {
                return `-plus-${name}`;
            }
            return '';
        }

        // Detect if we need to add to the default case name in various ways
        // Protein Chains: changed if any polymer is added or if the number of chains does not
        // match that of the original simspec
        const chains = newVals.polymers.concat(newVals.proteinChains)
            .map((p) => p.chain).sort();
        const allChains = allVals.polymers.concat(allVals.proteinChains)
            .map((p) => p.chain);
        const chainsChanged = newVals.polymers.length > 0
            || newVals.proteinChains.length < allVals.proteinChains.length;

        let caseTag = '';
        // Add names of included chains if not the default chains
        if (chainsChanged) {
            if (chains.length > 4) {
                caseTag += `_with-${chains.length}-chains`;
            } else if (chains.length > 0) {
                const base = chains.length === 1 ? '_chain-' : '_chains-';
                const chainStr = chains.join('-');
                caseTag += `${base}${chainStr}`;
            }
        }
        caseTag += getModTag(newVals.cofactors, allVals.cofactors, chains, allChains, 'cofactor');
        caseTag += getModTag(newVals.ions, allVals.ions, chains, allChains, 'ion');
        return caseTag;
    }

    function updateSimInputs({
        compoundInput, proteinChain, ion, cofactor, polymer,
    }) {
        // Used to toggle a value, item, in a list of form values
        const toggle = (list, item) => {
            if (list.indexOf(item) >= 0) {
                return list.filter((i) => i !== item);
            } else {
                list.push(item);
                return list;
            }
        };
        // If toggling a value, item, that is connected to other form values, otherItems,
        // this will toggle those as well
        const toggleSecondaries = (list, item, otherList, otherItems) => {
            const chainItems = otherItems.filter((i) => i.chain === item.chain);
            let newOtherList = otherList;

            if (list.indexOf(item) >= 0) {
                for (const chainItem of chainItems) {
                    if (newOtherList.indexOf(chainItem) >= 0) {
                        newOtherList = toggle(newOtherList, chainItem);
                    }
                }
            } else {
                for (const chainItem of chainItems) {
                    if (newOtherList.indexOf(chainItem) === -1) {
                        newOtherList = toggle(newOtherList, chainItem);
                    }
                }
            }
            return newOtherList;
        };

        let {
            compound, proteinChains, ions, cofactors, polymers,
        } = formValues;

        // Depeneding on which input is being changed, adjust the associated list
        // ProteinChains and Polymers affect ions/cofactors, they are toggled in toggleSecondaries
        if (compoundInput) compound = (compoundInput === 'noCompound') ? null : compoundInput;
        if (proteinChain) {
            ions = toggleSecondaries(proteinChains, proteinChain, ions, formOptions.ions);
            cofactors = toggleSecondaries(
                proteinChains, proteinChain, cofactors, formOptions.cofactors
            );
            proteinChains = toggle(proteinChains, proteinChain);
        }
        if (polymer) {
            ions = toggleSecondaries(polymers, polymer, ions, formOptions.ions);
            cofactors = toggleSecondaries(polymers, polymer, cofactors, formOptions.cofactors);
            polymers = toggle(polymers, polymer);
        }
        if (ion) {
            ions = toggle(ions, ion);
        }
        if (cofactor) {
            cofactors = toggle(cofactors, cofactor);
        }

        let caseName = SimSpec.defaultCaseName({
            protein: formOptions.protein, compound, simType: simulationType?.type,
        });
        // Don't update the case name if it is blank
        if (caseName) {
            caseName += getCaseNameTag({
                compound, proteinChains, ions, cofactors, polymers,
            }, formOptions);
        }

        handleWarnings(simulationType.type);
        setFormValues({
            ...formValues,
            case: caseName,
            compound,
            proteinChains,
            ions,
            cofactors,
            polymers,
        });
    }

    // A selected additional structure can reuse its existing server-side setup --
    // no prep pass, args derive synchronously from the MapCase -- when "use existing
    // setup" is on and it has prepared data (a prepared-setup source: Public/Fragserv/
    // Managed). This works for not-yet-loaded structures (e.g. curated-panel members).
    // Everything else goes through the Prepare System pass.
    function canReuseExistingSetup(protein) {
        return useExistingForAdditional && isPreparedSetupSource(protein.sourceDesc);
    }

    // Fan-out submission args for a reuse structure, built from its MapCase (existing
    // project/case) -- needs no workspace CaseData.
    function reuseSetupArgs(protein) {
        return SimSpec.fragservArgsFromProtein(protein, fragmentsToSend(simulationType.type));
    }

    async function prepareAdditionalStructures(structures) {
        // Prepare each additional target that needs preparation (reuse structures are
        // resolved reactively, not here). Per-structure custom binding-site is v2.
        const fragments = fragmentsToSend(simulationType.type);
        const results = [];
        for (const protein of structures) {
            const caseData = App.Workspace.lookupCaseData(protein);
            const simSpec = SimSpec.GetFromWorkspace(App.Workspace, caseData, false, fragments);
            // Prep mutates shared workspace selection, so structures prep in series.
            // eslint-disable-next-line no-await-in-loop
            const { status, errorInfo } = await UserActions.PrepareProjectCase({ simSpec });
            const ready = status === SimSpec.Status.Ready;
            results.push({
                name: protein.getLongName(),
                uri: protein.uri,
                status,
                fragservArgs: ready ? simSpec.forFragserv : null,
                error: ready ? null : describePrepError(status, errorInfo),
            });
        }
        return results;
    }

    async function handlePrepSubmit(evt) {
        evt.preventDefault();
        if (bindingSiteIncomplete) return; // Prepare System is disabled; also block Enter
        setLoading(true);
        // Primary is reusing its existing/default setup: don't re-prepare it (that could
        // collide with the existing checkpoint's structure name). Keep the primary's
        // default fragserv config and just prepare any additional targets selected for the
        // multi-protein fan-out.
        const usingDefaultConfig = allowDefaultConfig[simulationType.type] && useDefaultConfig;
        if (usingDefaultConfig && !useSelection) {
            const additional = selectedProteins.filter(
                (p) => p && p !== selectedProtein && !canReuseExistingSetup(p)
            );
            setWarning(false);
            setFragservData(defaultConfig);
            setFormDisabled(true);
            setPrepared(true);
            setAdditionalPrepared(
                additional.length > 0 ? await prepareAdditionalStructures(additional) : []
            );
            setLoading(false);
            return;
        }
        // In the case of useSelection, SimSpec only needs protein, project, and case
        let newSimSpec;
        if (useSelection) {
            newSimSpec = new SimSpec({
                protein: selectedProtein,
                project: formValues.project,
                case: formValues.case,
                bindingSiteSpecs: formValues.bindingSiteSpecs,
                autoBindingSite: formValues.autoBindingSite,
                fragments: fragmentsToSend(simulationType.type),
            });
        } else {
            newSimSpec = new SimSpec({
                protein: selectedProtein,
                proteinChains: formValues.proteinChains,
                compound: formValues.compound,
                ions: formValues.ions,
                cofactors: formValues.cofactors,
                polymers: formValues.polymers,
                project: formValues.project,
                case: formValues.case,
                bindingSiteSpecs: formValues.bindingSiteSpecs,
                autoBindingSite: formValues.autoBindingSite,
                fragments: fragmentsToSend(simulationType.type),
            });
        }

        if (filterAlphaFold) {
            newSimSpec = alphaFoldSimSpecFilter(newSimSpec);
        }

        const {
            status, case_suggestion, errorInfo,
        } = await UserActions.PrepareProjectCase({
            simSpec: newSimSpec,
            useSelection,
            dontDeselect: keepSelection,
        });

        switch (status) {
            case SimSpec.Status.Ready: {
                const fragservArgs = newSimSpec.forFragserv;
                setWarning(false);
                setFragservData(fragservArgs);
                setFormDisabled(true);
                setPrepared(true);
                // If not using selection, PrepareProjectCase will remove selection
                if (!useSelection) setWorkspaceSelection([]);
                // Multi-protein: also prepare any additional target structures
                // (each with its own default config) for the fan-out submission.
                const additional = selectedProteins.filter(
                    (p) => p && p !== selectedProtein && !canReuseExistingSetup(p)
                );
                setAdditionalPrepared(
                    additional.length > 0 ? await prepareAdditionalStructures(additional) : []
                );
                break;
            }
            case SimSpec.Status.Conflict: {
                const usingSuggestion = case_suggestion && case_suggestion !== formValues.case;
                let msg = `The structure name you provided (${formValues.case}) was already used for a different complex. `;
                if (usingSuggestion) {
                    msg += 'Use the suggested alternative or choose a different structure name.';
                } else {
                    msg += 'Try a different structure name.';
                }
                // Adding fragments to an existing structure is a supported workflow, and it is
                // very likely what the user wanted if they typed the structure's own name. Point
                // them at it -- but only for fragment runs (waters and hotspots always belong in
                // a new case), and only when the clash is with the structure being simulated
                // from, since that is the one "keep using the current setup" would reuse.
                if (simulationType?.isFragment && errorInfo?.collidesWithSource) {
                    msg += ' To add fragments to the existing structure instead, choose "Keep using the current simulation setup".';
                }
                setWarning({ warning: msg });
                if (usingSuggestion) {
                    updateCaseOrProject('case', case_suggestion);
                }
                break;
            }
            case SimSpec.Status.StorageUpdatedError:
                setWarning({ warning: 'Your account storage was recently updated and needs to be refreshed; please try signing out and in again.' });
                break;
            case SimSpec.Status.PersonalStorageError:
                setWarning({ warning: 'Your account storage is still getting set up; please try signing out and in again.' });
                break;
            case SimSpec.Status.FdbFileError:
            case SimSpec.Status.PartialChargesError:
                setWarning({ warning: `This complex contains one or more residues that are not currently supported for simulations - ${errorInfo.failedResidues.join(', ')}. You may be able to deselect the problematic chain(s) to continue.` });
                break;
            case SimSpec.Status.NoHydrogensError:
                setWarning({ warning: 'Cannot run fragment simulation on this structure. No hydrogens found. (Note: BMaps currently does not support computing hydrogens for certain large structures from the PDB)' });
                break;
            case SimSpec.Status.UnknownError:
                setWarning({ warning: 'There was an error trying to prepare the simulation files.' });
                break;
            case SimSpec.Status.TooManyChainsError:
                setWarning({ warning: 'This system has more than 500 residues and is too large for simulation. Try removing some chains.' });
                break;
            case SimSpec.Status.BindingSiteSpecError:
                setWarning({ warning: `Binding site could not be applied: ${errorInfo.bindingSiteSpecErr}` });
                break;
            case SimSpec.Status.NoProteinChainsError:
                setWarning({ warning: 'Cannot run fragment simulation on this structure. No protein or nucleotide chains found.' });
                break;
            default:
                // This shouldn't happen
                setWarning({ warning: 'There was a problem trying to prepare the simulation files.' });
        }
        setLoading(false);
    }

    const useDefault = allowDefaultConfig[simulationType.type] && useDefaultConfig;

    const shouldShowGen2Annealing = simulationType.allowGen2;
    const effectiveGen2Annealing = simulationType.allowGen2 && gen2Annealing;
    const shouldShowSolvation = simulationType.allowSolvation && Loader.AllowLabFeatures;
    const effectiveEnableSolvation = simulationType.allowSolvation && enableSolvation;

    const fragservDataToUse = fragservData && {
        ...fragservData,
        // Stringify extraSimSettings because it going in form data
        extraSimSettings: JSON.stringify({
            isClustering: simulationType.isClustering,
            enableSolvation: effectiveEnableSolvation,
            minimizeBox,
            gen2Annealing: effectiveGen2Annealing,
            // Prefer to skip annealing, but the solvation model will need it.
            skipAnnealing: effectiveGen2Annealing && !effectiveEnableSolvation,
        }),
    };

    // Multi-protein: the additional structures the user has currently selected to also
    // simulate (beyond the primary).
    // Additional targets: every selected structure except the primary, deduped by
    // structureKey so a structure selected via two routes (e.g. the primary that is
    // also a curated-panel member) is never submitted or billed twice.
    const primaryStructureKey = structureKey(selectedProtein);
    const seenTargetKeys = new Set(primaryStructureKey ? [primaryStructureKey] : []);
    const additionalTargets = selectedProteins.filter((p) => {
        if (!p || p === selectedProtein) return false;
        const key = structureKey(p);
        if (!key || seenTargetKeys.has(key)) return false;
        seenTargetKeys.add(key);
        return true;
    });
    // Disambiguated labels for every structure in this submission (primary + additionals),
    // so two cases of one kinase read distinctly in the readiness breakdown.
    const submittedLabels = structureLabelsByUri(
        [selectedProtein, ...additionalTargets].filter(Boolean),
    );
    // Split additionals into those reusing an existing setup (resolved reactively from
    // the MapCase -- no prep pass) and those that must go through "Prepare System".
    const reuseAdditional = additionalTargets.filter(canReuseExistingSetup);
    const prepAdditional = additionalTargets.filter((p) => !canReuseExistingSetup(p));
    const reuseArgs = reuseAdditional.map(reuseSetupArgs);
    const selectedPrepNames = new Set(prepAdditional.map((p) => p.getLongName()));

    // Gather the prepared target payloads. Only count prep results for structures that
    // are still selected, so unchecking after preparation drops them from the submission.
    const successfulAdditional = additionalPrepared.filter(
        (a) => a.status === SimSpec.Status.Ready && selectedPrepNames.has(a.name)
    );
    const failedAdditional = additionalPrepared.filter(
        (a) => a.status !== SimSpec.Status.Ready && selectedPrepNames.has(a.name)
    );
    const allTargetArgsUncapped = fragservData
        ? [fragservData, ...reuseArgs, ...successfulAdditional.map((a) => a.fragservArgs)]
        : [];
    const allTargetArgs = allTargetArgsUncapped.slice(0, MAX_PROTEINS_PER_CART);
    const overProteinCap = allTargetArgsUncapped.length > MAX_PROTEINS_PER_CART;
    const isMultiTarget = allTargetArgs.length > 1;
    // Hidden form fields: legacy flat for a single target; a JSON `proteins`
    // array plus shared fragments/settings for multi-protein (Risk R7).
    let submitFields = fragservDataToUse;
    if (fragservDataToUse && isMultiTarget) {
        submitFields = {
            proteins: JSON.stringify(allTargetArgs),
            fragments: fragservData.fragments || '',
            extraSimSettings: fragservDataToUse.extraSimSettings,
        };
    }

    // Names already run through a prep pass (whether they succeeded or were skipped).
    const processedAdditionalNames = new Set(additionalPrepared.map((a) => a.name));
    // Only prepare-needed additionals can be pending; reuse structures are ready
    // immediately (no prep pass). While any prep-needed structure is unprepared, the
    // form routes back through "Prepare System" instead of the ready state.
    const additionalPrepPending = prepAdditional.some(
        (p) => !processedAdditionalNames.has(p.getLongName())
    );
    const readyToSubmit = !!submitFields && !additionalPrepPending;
    return (
        <DialogContent>
            <form onSubmit={handlePrepSubmit} id={formId}>
                <DialogContentSection title="Simulation Type" disablePaddingTop disablePaddingBottom>
                    <FormControl required>
                        <RadioGroup
                            name="simType"
                            onChange={handleSimTypeChange}
                            style={{ width: '100%', flexDirection: 'row' }}
                        >
                            <FormControlLabel
                                control={<Radio checked={simulationType.isWater} />}
                                label="Water"
                                value="water"
                            />
                            <FormControlLabel
                                control={<Radio checked={simulationType.isFragment} />}
                                label="Fragment"
                                value="fragment"
                            />
                            <FormControlLabel
                                control={<Radio checked={simulationType.isClustering} />}
                                label="Hot Spots"
                                value="clustering"
                            />
                        </RadioGroup>
                    </FormControl>
                    <SimTypeDescription simType={simulationType.type} />
                </DialogContentSection>
                <DialogContentSection title="Simulation System">
                    <ProteinSetupBox
                        loadedProteins={loadedProteins}
                        updateSimInputs={updateSimInputs}
                        formOptions={formOptions}
                        formValues={formValues}
                        isWaterSim={simulationType.isWater}
                        disabled={prepared || useSelection}
                        multiSelectDisabled={useSelection || loading}
                        allowDefaultConfig={allowDefaultConfig[simulationType.type]}
                        useDefaultConfig={useDefaultConfig}
                        onUseDefaultChange={handleDefaultConfigToggle}
                        selectedProteins={selectedProteins}
                        useExistingForAdditional={useExistingForAdditional}
                        onToggleProtein={toggleSelectedProtein}
                        onSetSelectedProteins={setSelectedProteins}
                        onSetUseExisting={setUseExistingForAdditional}
                    />
                </DialogContentSection>
                <DialogContentSection
                    title="Advanced Parameters"
                    disablePaddingTop
                    disablePaddingRight
                    disablePaddingBottom
                    collapsible
                >
                    <BindingSiteSpecs
                        setValue={({ bindingSiteSpecs, autoBindingSite, incomplete }) => {
                            setBindingSiteIncomplete(!!incomplete);
                            setFormValues({
                                ...formValues,
                                bindingSiteSpecs,
                                autoBindingSite,
                            });
                        }}
                        disabled={prepared}
                        selectionAlreadyUsed={useSelection}
                        automaticHint={automaticBindingSiteHint({
                            useSelection,
                            compound: formValues.compound,
                            cofactors: formValues.cofactors,
                        })}
                    />
                    <br />
                    {shouldShowSolvation && (
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={enableSolvation}
                                onChange={(evt) => setEnableSolvation(evt.target.checked)}
                            />
                        )}
                        label="Enable Solvation Model"
                    />
                    )}
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={minimizeBox}
                                onChange={(evt) => setMinimizeBox(evt.target.checked)}
                            />
                        )}
                        label="Minimize Box"
                    />
                    {shouldShowGen2Annealing && (
                    <>
                        <br />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={gen2Annealing}
                                    onChange={(evt) => setGen2Annealing(evt.target.checked)}
                                />
                            )}
                            label={(
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                    Use Focused Fragment Annealing (FFA)
                                    <TooltipInfo title="Focused Fragment Annealing is a fragment simulation method that achieves comparable sampling much faster than full SACP (Simulated Annealing of Chemical Potential)." />
                                </Box>
                            )}
                        />
                    </>
                    )}
                    <br />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={filterAlphaFold}
                                onChange={(evt) => handleFilterAlphaFoldToggle(evt.target.checked)}
                                disabled={prepared
                                    || !formOptions.isAlphaFoldStructure || useSelection}
                            />
                        )}
                        label="Filter out low confidence AlphaFold residues"
                    />
                    <br />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={useSelection && !prepared}
                                onChange={(evt) => handleUseSelectionToggle(evt.target.checked)}
                                disabled={workspaceSelection.length === 0 || prepared
                                    || !selectedProtein || filterAlphaFold}
                            />
                        )}
                        label={`Use Selection (${workspaceSelection.length} atoms selected)`}
                    />
                    { !!Loader.AllowLabFeatures && (
                    <>
                        <br />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={keepSelection}
                                    onChange={(evt) => setKeepSelection(evt.target.checked)}
                                    disabled={prepared || !selectedProtein}
                                />
                            )}
                            label="Keep selection after structure preparation (dev / lab only)"
                        />
                    </>
                    )}
                </DialogContentSection>
                <DialogContentSection title="System Name">
                    <TextInputWithTooltip
                        id="targetProteinName"
                        disabled={formDisabled && prepared}
                        labelText="Target Protein Name"
                        tooltip="Target protein name groups all structures and simulation data for
                                a certain target (eg. 'factorIXa')"
                        value={!useDefault ? formValues.project : ' '}
                        title={useDefault ? `Using existing configuration: ${defaultConfig.project}/${defaultConfig.case}`: ''}
                        onChange={(evt) => {
                            updateCaseOrProject('project', evt.target.value);
                        }}
                        validate={
                            prepared ? undefined : (value) => (
                                (ProjectNameRegex.test(value)) ? '' : ProjectNameRegexDescription
                            )
                        }
                    />
                    <TextInputWithTooltip
                        id="structureName"
                        disabled={formDisabled && prepared}
                        labelText="Structure Name"
                        tooltip="Structure name groups fragment data for one complex around one
                                structure (eg. '5EGM', '4YZU+4K6')"
                        value={!useDefault ? formValues.case : ' '}
                        title={useDefault ? `Using existing configuration: ${defaultConfig.project}/${defaultConfig.case}`: ''}
                        onChange={(evt) => updateCaseOrProject('case', evt.target.value)}
                        validate={
                            prepared ? undefined : (value) => (
                                (ProjectNameRegex.test(value)) ? '' : ProjectNameRegexDescription
                            )
                        }
                    />
                </DialogContentSection>
            </form>
            {warning
                && (
                    <Typography sx={{ paddingBottom: '10px', color: 'error.main' }}>
                        Error:
                        {' '}
                        {warning.warning}
                    </Typography>
                )}
            <Box style={{ margin: '0 0 10px' }}>
                {readyToSubmit
                    ? (
                        <>
                            {/* Per-structure breakdown -- shown whenever the form is ready (not
                                only when additionals are selected) so the explanation is
                                consistent between single- and multi-structure submissions. */}
                            {selectedProtein && (
                                <Box sx={{ paddingBottom: '8px' }}>
                                    <Typography sx={{ color: 'success.main' }}>
                                        {`${submittedLabels.get(selectedProtein?.uri) || 'Primary structure'} ✓ ${useDefault ? 'using existing setup' : 'prepared'}`}
                                    </Typography>
                                    {reuseAdditional.map((p) => (
                                        <Typography key={p.uri} sx={{ color: 'success.main' }}>
                                            {`${submittedLabels.get(p.uri)} ✓ using existing setup`}
                                        </Typography>
                                    ))}
                                    {successfulAdditional.map((a) => (
                                        <Typography key={a.name} sx={{ color: 'success.main' }}>
                                            {`${submittedLabels.get(a.uri) ?? a.name} ✓ prepared`}
                                        </Typography>
                                    ))}
                                    {failedAdditional.map((a) => (
                                        <Typography key={a.name} sx={{ color: 'error.main' }}>
                                            {`${submittedLabels.get(a.uri) ?? a.name} ✗ skipped (${a.error})`}
                                        </Typography>
                                    ))}
                                    {isMultiTarget && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            Each structure uses its own default binding site
                                            (per-structure binding-site selection is coming soon).
                                        </Typography>
                                    )}
                                    {overProteinCap && (
                                        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
                                            {`Only the first ${MAX_PROTEINS_PER_CART} target structures will be submitted.`}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            <form id="submitSim" action="/services/shopping/loadCart" method="POST" target="_blank" onSubmit={() => setTimeout(submitClose, 1000)}>
                                {
                                    Object.keys(submitFields).map((k) => (
                                        <React.Fragment key={k}>
                                            <input
                                                key={k}
                                                type="hidden"
                                                name={k}
                                                title={k}
                                                value={submitFields[k]}
                                                readOnly
                                            />
                                        </React.Fragment>
                                    ))
                                }
                                <Box
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                    }}
                                >
                                    <Typography sx={{ color: 'primary.main' }}>
                                        {isMultiTarget
                                            ? `${allTargetArgs.length} systems are ready for simulation`
                                            : 'This system is ready for simulation'}
                                    </Typography>
                                    <DialogSubmitButton form="submitSim" disabled={!permissions[simulationType.type]}>
                                        Continue to Simulator
                                    </DialogSubmitButton>
                                </Box>
                            </form>
                        </>
                    ) : (
                        <>
                            {loading
                                ? (
                                    <LinearProgress style={{ margin: '14px 0 30px' }} />
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        {bindingSiteIncomplete && (
                                            <Typography variant="body2" sx={{ color: 'warning.main' }}>
                                                {customBindingSiteIncompleteText}
                                            </Typography>
                                        )}
                                        <DialogSubmitButton
                                            form={formId}
                                            disabled={!permissions[simulationType.type]
                                                || bindingSiteIncomplete}
                                        >
                                            Prepare System
                                        </DialogSubmitButton>
                                    </Box>
                                )}
                        </>
                    )}
            </Box>
            <DialogContentText>
                Note: Simulation runs are fulfilled by the simulation shopping cart, which
                opens in a separate page.
            </DialogContentText>
            <ExternalLink link="/services/runs/runs">
                View simulation status
            </ExternalLink>
        </DialogContent>
    );
}

function describePrepError(status, errorInfo) {
    switch (status) {
        case SimSpec.Status.Conflict:
            return 'structure name conflict';
        case SimSpec.Status.TooManyChainsError:
            return 'too many residues';
        case SimSpec.Status.NoHydrogensError:
            return 'no hydrogens';
        case SimSpec.Status.NoProteinChainsError:
            return 'no protein/nucleotide chains';
        case SimSpec.Status.FdbFileError:
        case SimSpec.Status.PartialChargesError:
            return errorInfo?.failedResidues
                ? `unsupported residue(s): ${errorInfo.failedResidues.join(', ')}`
                : 'unsupported residue(s)';
        case SimSpec.Status.BindingSiteSpecError:
            return 'binding site could not be applied';
        case SimSpec.Status.StorageUpdatedError:
        case SimSpec.Status.PersonalStorageError:
            return 'account storage needs refresh';
        default:
            return 'preparation failed';
    }
}

function CompoundSelect({
    compounds, selected, onChange, disabled,
}) {
    if (compounds && compounds.length > 0) {
        return (
            <FormControl
                style={{ width: '250px', margin: '10px 0 24px 6px' }}
            >
                <Select
                    value={selected || 'noCompound'}
                    disabled={disabled}
                    label="Compound"
                    onChange={(evt) => {
                        onChange(compounds.find((c) => c.resSpec === evt.target.value) || 'noCompound');
                    }}
                >
                    <option value="noCompound">No compound</option>
                    {compounds.map((cmp) => (
                        <option value={cmp.resSpec} key={cmp.resSpec}>
                            {cmp.resSpec}
                        </option>
                    ))}
                </Select>
            </FormControl>
        );
    } else {
        return (
            <FormControl
                style={{ width: '250px', margin: '0 0 24px 6px' }}
            >
                <Select
                    value="noCompound"
                    label="Compound"
                    onChange={onChange}
                    style={{ width: '100%' }}
                    disabled
                >
                    <option value="noCompound">No compounds available</option>
                </Select>
            </FormControl>
        );
    }
}

/**
 * Note: useDefaultConfig disables and clears all the checkboxes.
 * This is because "default config" means "use whatever is in the server checkpoint file."
 * It's not possible for BMaps to know how to assign the checkboxes. This approach clears them
 * all so that by being hopefully obviously wrong, it is hopefully less confusing.
 */
function CheckboxList({
    label, items, selected, getName, onChange, disabled, useDefaultConfig,
}) {
    return (
        <FormGroup>
            <FormLabel>{`${label}:`}</FormLabel>
            {items && items.map((i) => (
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={!useDefaultConfig && selected?.includes(i)}
                            onChange={() => onChange(i)}
                            name={getName(i)}
                        />
                    )}
                    label={getName(i)}
                    key={getName(i)}
                    disabled={useDefaultConfig || disabled}
                />
            ))}
        </FormGroup>
    );
}

function SimTypeDescription({ simType }) {
    let content = '';
    switch (simType) {
        case 'water':
            content = '';
            break;
        case 'fragment':
            content = '';
            break;
        case 'clustering':
            content = 'Hot spot runs include 16 diverse clustering fragments.';
            break;
        // no default
    }
    return !!content && <div>{content}</div>;
}
